import { publishedSitesStore } from '@littlebird/sdk';
import {
  buildSharedSiteUrl,
  copyShareLink,
  type ShareLinkAdapter,
  type UseShareLinkReturn,
  useShareLink,
} from '@littlebird/ui-utils';
import { observer } from 'mobx-react-lite';
import { type ReactNode, useCallback, useMemo, useState } from 'react';

import { Icons } from '@/assets/icons/icons';
import { createShareNotify } from '@/components/blocks/share/share-notify';
import { ShareAccessControls } from '@/components/blocks/share/ShareAccessControls';
import { SharePopover } from '@/components/blocks/share/SharePopover';
import { Text, YStack } from '@/components/ui';
import { getEnvVar } from '@/env';
import { teamStore } from '@/features/team/team.store';
import { userStore } from '@/stores/user.store';
import { trackEvent } from '@/utils/analytics';

const BASE_URL = getEnvVar('BASE_URL_FOR_PUBLIC_LINKS');

const notify = createShareNotify(
  'Failed to share site',
  'Failed to share, please try again'
);

type ShareTarget = { fileId: string } | { spriteId: string };

interface SharePublishedSitePopoverProps {
  target: ShareTarget;
  children: ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /**
   * Position the popover against `children` without making it the trigger. For callers that open
   * the popover from elsewhere (e.g. a dropdown item) but want it anchored to a visible element.
   */
  anchorOnly?: boolean;
}

export const SharePublishedSitePopover = observer(
  ({
    target,
    children,
    open: controlledOpen,
    onOpenChange,
    anchorOnly = false,
  }: SharePublishedSitePopoverProps) => {
    const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
    const open = controlledOpen ?? uncontrolledOpen;
    const setOpen = useCallback(
      (next: boolean) => {
        setUncontrolledOpen(next);
        onOpenChange?.(next);
      },
      [onOpenChange]
    );
    const baseUrl = BASE_URL ?? window.location.origin;

    const isSprite = 'spriteId' in target;
    const id = isSprite ? target.spriteId : target.fileId;

    const adapter = useMemo<ShareLinkAdapter>(
      () => ({
        getStatus: (shareId) =>
          isSprite
            ? publishedSitesStore.getSpriteStatus(shareId)
            : publishedSitesStore.getStatus(shareId),
        setVisibility: async (shareId, visibility: 'team' | 'public') => {
          const status = await (isSprite
            ? publishedSitesStore.setSpriteVisibility(shareId, visibility)
            : publishedSitesStore.setVisibility(shareId, visibility));
          if (isSprite)
            trackEvent('SPRITE_VISIBILITY_CHANGED', {
              sprite_id: shareId,
              visibility,
            });
          return status;
        },
        revoke: async (shareId) => {
          await (isSprite
            ? publishedSitesStore.revokeSprite(shareId)
            : publishedSitesStore.revoke(shareId));
          // Revoking the link makes the sprite private again.
          if (isSprite)
            trackEvent('SPRITE_VISIBILITY_CHANGED', {
              sprite_id: shareId,
              visibility: 'private',
            });
        },
        buildUrl: (_ids, tokens) => buildSharedSiteUrl(tokens[0]!, baseUrl),
        onLinkCreated: (url) => copyShareLink(url, notify),
        ...notify,
      }),
      [baseUrl, isSprite]
    );

    const link = useShareLink({
      ids: [id],
      adapter,
      open,
      supportsTeam: true,
      canShareBeyondPrivate: !userStore.hasSignedBaa,
      hasTeam: !!teamStore.dashboard,
    });

    return (
      <SharePopover
        anchor={children}
        anchorOnly={anchorOnly}
        open={open}
        onOpenChange={setOpen}
        title="Share artifact"
        description="Choose who can open this artifact with a link"
      >
        <YStack className="gap-3">
          <ShareAccessControls
            link={link}
            idPrefix="share-site"
            linkFooter={<UpdateLinkBanner link={link} />}
          />
          {userStore.hasSignedBaa && (
            <Text size="xs" variant="muted" className="pb-3.5">
              Sharing is disabled for your account.
            </Text>
          )}
        </YStack>
      </SharePopover>
    );
  }
);

const UpdateLinkBanner = ({ link }: { link: UseShareLinkReturn }) => {
  if (!link.shareStatus?.hasNewMessages) return null;
  const visibility = link.selectedVisibility === 'team' ? 'team' : 'public';

  return (
    <Text size="xs" variant="muted">
      This artifact changed since you shared it.{' '}
      <button
        type="button"
        className="inline-flex items-center gap-1 text-foreground underline underline-offset-4 hover:text-foreground/80 transition-colors disabled:opacity-50"
        onClick={() => link.handleCreateOrUpdateLink(visibility)}
        disabled={link.isCreating}
      >
        {link.isCreating ? (
          <>
            <Icons.loader className="size-3 animate-spin shrink-0" />
            Updating…
          </>
        ) : (
          <>
            <Icons.refresh className="size-3 shrink-0" />
            Update link
          </>
        )}
      </button>
    </Text>
  );
};
